import { Injectable } from '@angular/core';
import { OAuthService } from 'angular-oauth2-oidc';
import { BehaviorSubject, distinctUntilChanged, filter, take } from 'rxjs';
import { authConfig } from 'src/app/common/authentification/auth.config';
import { ErrorService } from '../error';
import { AuthenticationService } from '../store/authentication/authentication.service';
import { LoginModel, UserConnected } from '../store/authentication/types';
import { UserService } from '../store/user/user.service';

@Injectable({ providedIn: 'root' })
export class OAuthenticationService {
  private _isAuthenticated$ = new BehaviorSubject<boolean>(false);
  public readonly isAuthenticated$ = this._isAuthenticated$.asObservable();

  private initialExpiresIn: number = 0;

  constructor(
    private readonly oauthService: OAuthService,
    private readonly authenticationService: AuthenticationService,
    private readonly errorService: ErrorService,
    private readonly userService: UserService,
  ) {
    // Always configure first
    this.oauthService.configure(authConfig);

    this.oauthService.setStorage(localStorage);

    let latestUser: UserConnected | undefined;
    this.authenticationService.getUserConnected().subscribe((user) => {
      latestUser = user;
    });

    if (this.oauthService.hasValidAccessToken()) {
      if (!latestUser || (!latestUser.email && !latestUser.username)) {
        this.setUserFromClaims();
      }
      this.setIsAuthenticated();
    }

    this.oauthService.events.subscribe((event) => {
      switch (event.type) {
        case 'user_profile_loaded':
          if (!latestUser || (!latestUser.email && !latestUser.username)) {
            this.setUserFromClaims();
          }
          this.setIsAuthenticated();
          break;
        case 'logout':
          this.authenticationService.setUserConnected({
            username: '',
            email: '',
          });
          this.userService.getCurrentUser.reset();
          this.setIsAuthenticated();
          break;
        case 'session_terminated':
        case 'token_refresh_error':
          this.logout();
          this.setIsAuthenticated();
          break;
        case 'token_received':
          this.initialExpiresIn = this.accessTokenExpiresIn;
          // console.log('token received');
          // this.userService.getServerTime
          //   .call()
          //   .pipe(filterTrue(), take(1))
          //   .subscribe((serverTime) => {
          //     console.log('serverTime expoch', serverTime);
          //     console.log('serverTime', new Date(serverTime));
          //     console.log('datetimeNow', new Date(Date.now()));
          //     this.userService.getServerTime.reset();
          //   });
          this.setIsAuthenticated();
          break;
        case 'token_expires':
          // console.log('token expires');
          // this.percentageAccessToken;
          // this.refreshToken();
          break;
      }
    });

    this.authenticationService
      .getUserConnected()
      .pipe(
        distinctUntilChanged(),
        filter(() => this.isAuthenticated()),
      )
      .subscribe(() => {
        this.userService.getCurrentUser.call();
      });

    this.authenticationService
      .getUserConnected()
      .pipe(
        distinctUntilChanged(),
        filter((x) => this.isAuthenticated() && !x.email && !x.username),
        take(1),
      )
      .subscribe(() => {
        this.setUserFromClaims();
      });

    // Check how to make silent refresh work properly (get the silent refresh iframe )
    // if (!this.silentRefreshSetupDone) {
    //   this.oauthService.setupAutomaticSilentRefresh();
    //   this.silentRefreshSetupDone = true;
    // }
  }

  public loginWithCredentials(loginModel: LoginModel): Promise<any> {
    this.oauthService.oidc = false;
    return this.oauthService
      .fetchTokenUsingPasswordFlow(loginModel.mail, loginModel.password)
      .then(() => this.oauthService.loadUserProfile())
      .catch((error) => {
        throw this.errorService.serverErrorRedirect(error);
      });
  }

  public getUserProfile(): Promise<any> {
    this.oauthService.oidc = false;
    return this.oauthService.loadUserProfile();
  }

  public logout(): void {
    this.oauthService.logOut();
  }

  public isAuthenticated(): boolean {
    return this._isAuthenticated$.value && !this.isAccessTokenExpired;
  }

  public getIsAuthenticated() {
    return this.isAuthenticated$;
  }

  public setIsAuthenticated(): void {
    const isAuthenticated =
      this.oauthService.hasValidAccessToken() && !this.isAccessTokenExpired;
    if (this._isAuthenticated$.value !== isAuthenticated) {
      this._isAuthenticated$.next(isAuthenticated);
    }
  }

  private setValueToIsAuthenticated(value: boolean): void {
    this._isAuthenticated$.next(value);
  }

  public refreshToken(): Promise<boolean> {
    this.setValueToIsAuthenticated(false);
    return this.oauthService
      .refreshToken()
      .then((result) => {
        console.log('refresh');
        this.setIsAuthenticated();
        return !!result;
      })
      .catch(() => false);
  }

  public isAuthenticatedOrRefresh(): Promise<boolean> {
    if (this.isAuthenticated()) {
      return new Promise<boolean>((resolve) => {
        resolve(true);
      });
    } else {
      return this.refreshToken();
    }
  }

  private setUserFromClaims(): void {
    const claims: any = this.oauthService.getIdentityClaims();
    if (!claims || typeof claims !== 'object') {
      return;
    }
    const username =
      claims['username'] || claims['preferred_username'] || claims['sub'] || '';
    const email = claims['email'] || '';
    if (username || email) {
      const user: UserConnected = { username, email };
      this.authenticationService.setUserConnected(user);
    }
  }

  private get isAccessTokenExpired() {
    // custom check of token expiration to avoid bug in library: https://github.com/manfredsteyer/angular-oauth2-oidc/issues/1135
    const expiresAt = this.oauthService.getAccessTokenExpiration() as
      | number
      | null; // can return null https://github.com/manfredsteyer/angular-oauth2-oidc/blob/12.1/projects/lib/src/oauth-service.ts#L2354
    if (!expiresAt) return true;
    // console.log('expires at', new Date(expiresAt));

    // console.log(
    //   'expires at (after skew)',
    //   (expiresAt ?? 0) - (this.oauthService.clockSkewInSec ?? 0) * 1000,
    // );

    return (
      Date.now() >
      (expiresAt ?? 0) - (this.oauthService.clockSkewInSec ?? 0) * 1000
    );
  }

  // private get percentageAccessToken() {
  //   // custom check of token expiration to avoid bug in library: https://github.com/manfredsteyer/angular-oauth2-oidc/issues/1135
  //   const expiresAt = this.oauthService.getAccessTokenExpiration() as
  //     | number
  //     | null; // can return null https://github.com/manfredsteyer/angular-oauth2-oidc/blob/12.1/projects/lib/src/oauth-service.ts#L2354
  //
  //   if (!expiresAt) {
  //     // console.log('Access Token Percentage : 100%');
  //     return 100;
  //   }
  //   //When we reconnect, initialise a new one
  //   // if (!this.initialExpiresIn) this.initialExpiresIn = Date.now();
  //
  //   const actual = this.initialExpiresIn - this.accessTokenExpiresIn;
  //
  //   const percentage = (actual / this.initialExpiresIn) * 100;
  //
  //   if (!Number.isFinite(percentage)) return 100;
  //
  //   // console.log('Access Token Percentage : ' + percentage + '%');
  //   return percentage;
  // }

  private get accessTokenExpiresIn(): number {
    // custom check of token expiration to avoid bug in library: https://github.com/manfredsteyer/angular-oauth2-oidc/issues/1135
    const expiresAt = this.oauthService.getAccessTokenExpiration() as
      | number
      | null; // can return null https://github.com/manfredsteyer/angular-oauth2-oidc/blob/12.1/projects/lib/src/oauth-service.ts#L2354

    // console.log('access Token expires at', expiresAt);

    if (!expiresAt) return 0;

    return expiresAt - Date.now();
  }
}
